/*
 * Main Product Gallery — Swiper init + variant routing.
 *
 * Mounts on every `.product-main-gallery` element rendered by
 * snippets/product-main-gallery.liquid. The Liquid pre-renders
 * Swiper-shaped markup (.swiper > .swiper-wrapper > .swiper-slide
 * for both the main track and the thumbs strip); this script
 * builds the Swiper instances and wires them together.
 *
 * Settings come in via data-* attributes on the gallery root:
 *   data-layout            'slider' | 'scroll'  (Gallery: Layout)
 *   data-loop              'true'   | 'false'   (Loop Slider)
 *   data-autoplay          'true'   | 'false'   (Autoplay)
 *   data-autoplay-delay    milliseconds         (Autoplay Delay)
 *   data-show-arrows       'true'   | 'false'   (Show Arrows)
 *   data-show-bullets      'true'   | 'false'   (Show Pagination Bullets)
 *   data-show-thumbnails   'true'   | 'false'   (Show Thumbnails, > 1 media)
 *
 * Scroll mode (desktop only): when layout=scroll and viewport
 * ≥ 992px, the main slider stays as a vertical stack — no Swiper
 * is initialised on the main track and arrows / bullets / thumbs
 * are hidden. Mobile always swipes (Swiper still runs).
 *
 * Variant routing: listens for H3.0's `variant:update` event,
 * looks up the slide carrying `data-variant-id="{variant.id}"`,
 * and navigates the main Swiper to it. Slides without a
 * data-variant-id are media not tied to a specific variant and
 * are skipped by the lookup.
 *
 * Swiper is loaded as a UMD bundle in snippets/scripts.liquid,
 * exposing window.Swiper globally. See assets/testimonials.js for
 * the same init pattern.
 */

const Swiper = window.Swiper;

const SELECTOR = '.product-main-gallery';
const INSTANCE_KEY = '_productMainGalleryInstance';
const DESKTOP_QUERY = '(min-width: 992px)';

class ProductMainGallery {
  constructor(root) {
    this.root = root;
    this.layout = root.dataset.layout || 'slider';
    this.loop = root.dataset.loop === 'true';
    this.autoplayEnabled = root.dataset.autoplay === 'true';
    this.autoplayDelay = parseInt(root.dataset.autoplayDelay, 10) || 5000;
    this.showArrows = root.dataset.showArrows === 'true';
    this.showBullets = root.dataset.showBullets === 'true';
    this.showThumbnails = root.dataset.showThumbnails === 'true';

    this.mainEl = root.querySelector('.product-main-gallery__main');
    this.thumbsEl = root.querySelector('.product-main-gallery__thumbs');
    this.prevEl = root.querySelector('.product-main-gallery__arrow--prev');
    this.nextEl = root.querySelector('.product-main-gallery__arrow--next');
    this.bulletsEl = root.querySelector('.product-main-gallery__bullets');

    this.mainSwiper = null;
    this.thumbsSwiper = null;
    this.abortController = new AbortController();

    this.init();
  }

  init() {
    if (!this.mainEl) return;

    const slides = this.mainEl.querySelectorAll('.swiper-slide');
    const hasMultiple = slides.length > 1;

    // Desktop scroll mode: skip Swiper entirely, leave the static
    // vertical stack in place. Mobile (< 992px) always uses Swiper
    // even when the merchant picked Scroll for desktop.
    const isDesktopScroll =
      this.layout === 'scroll' && window.matchMedia(DESKTOP_QUERY).matches;

    if (isDesktopScroll || !hasMultiple) {
      this.root.classList.remove('not-loaded');
      // .is-static drives natural-aspect height + object-fit: contain —
      // correct for the desktop scroll stack, wrong for a single-slide
      // slider where the merchant set Section Height and expects the
      // image to fill it. Single-slide slider gets .is-initialized so
      // the default fill-the-container rules apply.
      if (isDesktopScroll) {
        this.root.classList.add('is-static');
        const column = this.root.closest('.product-main__grid--left');
        if (column) column.classList.add('product-main__grid--left--scroll');
      } else {
        this.root.classList.add('is-initialized');
      }
      this._bindVariantUpdate();
      return;
    }

    // Build thumbs swiper first (so the main can bind to it).
    // centerInsufficientSlides centres the strip horizontally when
    // the thumbs don't fill the available width; once they overflow,
    // freeMode lets the merchant scroll them naturally.
    //
    // observer + observeParents re-measure on container size changes,
    // covering the init-on-zero-width race (slides measured as 0px
    // at init lock the slider in `watchOverflow` mode — see the main
    // Swiper config for the matching guard).
    if (this.showThumbnails && this.thumbsEl) {
      this.thumbsSwiper = new Swiper(this.thumbsEl, {
        slidesPerView: 'auto',
        spaceBetween: 8,
        freeMode: true,
        watchSlidesProgress: true,
        slideToClickedSlide: true,
        centerInsufficientSlides: true,
        observer: true,
        observeParents: true,
      });
    }

    // watchOverflow: false — Swiper's default is to lock the slider
    // when slide widths sum < container width. At our init point, the
    // gallery's grid column hasn't always settled, so slides measure
    // 0px → Swiper thinks "everything fits" → locks navigation +
    // hides bullets + disables arrows even though there are real
    // multi-image slides. The static / single-slide branch above
    // already handles the "no slider needed" case, so we never need
    // Swiper's overflow auto-hide here.
    // observer + observeParents re-trigger Swiper's measurements when
    // the container resizes (customizer drawer toggles, viewport
    // changes, late-loading images settling layout).
    const mainOptions = {
      slidesPerView: 1,
      spaceBetween: 0,
      loop: this.loop && slides.length > 1,
      speed: 400,
      watchOverflow: false,
      observer: true,
      observeParents: true,
    };

    if (this.autoplayEnabled) {
      mainOptions.autoplay = {
        delay: this.autoplayDelay,
        disableOnInteraction: false,
        pauseOnMouseEnter: true,
      };
    }

    if (this.showArrows && this.prevEl && this.nextEl) {
      mainOptions.navigation = {
        prevEl: this.prevEl,
        nextEl: this.nextEl,
      };
    }

    if (this.showBullets && this.bulletsEl) {
      mainOptions.pagination = {
        el: this.bulletsEl,
        clickable: true,
      };
    }

    if (this.thumbsSwiper) {
      mainOptions.thumbs = { swiper: this.thumbsSwiper };
    }

    this.mainSwiper = new Swiper(this.mainEl, mainOptions);

    // Initial active-thumb sync. Swiper's Thumbs module relies on
    // `slidesPerViewDynamic()`, which needs computed slide widths
    // to run — with `slidesPerView: 'auto'` on the thumbs strip,
    // those widths aren't measured yet at init, so `thumbs.update()`
    // silently bails. A manual classList toggle keyed off the main
    // swiper's `realIndex` (loop-safe — `activeIndex` would be off by
    // the loop clones' count) sidesteps the timing dependency. The
    // slideChange binding keeps the marker synced through every
    // navigation including the variant-routed jumps.
    if (this.thumbsSwiper) {
      const sync = () => {
        const i = this.mainSwiper.realIndex;
        this.thumbsEl
          .querySelectorAll('.product-main-gallery__thumb')
          .forEach((t, n) => t.classList.toggle('swiper-slide-thumb-active', n === i));
      };
      sync();
      this.mainSwiper.on('slideChange', sync);
    }

    this.root.classList.remove('not-loaded');
    this.root.classList.add('is-initialized');

    this._bindVariantUpdate();
    this._bindCustomizerSelect(slides);
  }

  _bindVariantUpdate() {
    // Listen for H3.0's variant:update event and navigate the main
    // slider to the slide tied to the newly-selected variant. Scope
    // by .shopify-section so multiple PDPs on the page don't bleed.
    const section = this.root.closest('.shopify-section');
    const target = section || document;

    target.addEventListener(
      'variant:update',
      (event) => {
        const detail = event && event.detail;
        const resource = detail && detail.resource;
        const variantId = resource && resource.id;
        if (!variantId) return;
        this._goToVariant(String(variantId));
      },
      { signal: this.abortController.signal }
    );
  }

  _goToVariant(variantId) {
    const slide = this.mainEl.querySelector(
      `.swiper-slide[data-variant-id="${variantId}"]`
    );
    if (!slide) return;

    if (this.mainSwiper) {
      // For loop mode use the data-swiper-slide-index attribute Swiper
      // stamps on each real slide so slideToLoop targets the original
      // index rather than the duplicated clones.
      if (this.mainSwiper.params.loop) {
        const loopIndex = parseInt(slide.dataset.swiperSlideIndex, 10);
        if (!Number.isNaN(loopIndex)) {
          this.mainSwiper.slideToLoop(loopIndex);
          return;
        }
      }
      const slides = Array.from(this.mainEl.querySelectorAll('.swiper-slide'));
      const idx = slides.indexOf(slide);
      if (idx >= 0) this.mainSwiper.slideTo(idx);
    } else {
      // Static stack mode (desktop scroll). Scroll the matching slide
      // into view smoothly.
      slide.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  _bindCustomizerSelect(slides) {
    // shopify:block:select — merchant clicked a block in the
    // customizer tree. We don't have addressable slides in our schema
    // (media is product data, not blocks), so this is a no-op for
    // now. Wired here so we have a single place to extend if the
    // gallery later exposes slide-like blocks.
  }

  destroy() {
    this.abortController.abort();
    if (this.mainSwiper) {
      this.mainSwiper.destroy(true, true);
      this.mainSwiper = null;
    }
    if (this.thumbsSwiper) {
      this.thumbsSwiper.destroy(true, true);
      this.thumbsSwiper = null;
    }
  }
}

function initAll(root) {
  const scope = root || document;
  scope.querySelectorAll(SELECTOR).forEach((el) => {
    if (el[INSTANCE_KEY]) return;
    el[INSTANCE_KEY] = new ProductMainGallery(el);
  });
}

function destroyAll(root) {
  const scope = root || document;
  scope.querySelectorAll(SELECTOR).forEach((el) => {
    if (el[INSTANCE_KEY]) {
      el[INSTANCE_KEY].destroy();
      el[INSTANCE_KEY] = null;
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initAll());
} else {
  initAll();
}

document.addEventListener('shopify:section:load', (e) => initAll(e.target));
document.addEventListener('shopify:section:unload', (e) => destroyAll(e.target));
